import { injectGlobal } from '@emotion/css';
import { usageTracker, UsageTrackerEvent } from '@volumental/analytic';
import latoBoldExt from '@volumental/assets/fonts/lato-bold-ext.woff2';
import latoBold from '@volumental/assets/fonts/lato-bold.woff2';
import latoRegExt from '@volumental/assets/fonts/lato-reg-ext.woff2';
import latoReg from '@volumental/assets/fonts/lato-reg.woff2';
import { type JSX, useEffect } from 'react';
import { Navigation } from './navigation/navigation';
import { ApiProvider } from './providers/api-provider';
import { ConfigurationProvider } from './providers/configuration-provider';
import {
  useSessionStore,
  useInputStore,
  useViewStore,
  useScannerStore,
} from './store';
import { AppMode } from './types/types';

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
injectGlobal`
  html {
    font-size: 16px;
  }
  body {
    margin: 0px;
    padding: 0px;
  }
  :focus-visible {
    outline: 1px solid blue;
  }
  :root {
    color-scheme: only light;
  }

  /* latin-ext */
  @font-face {
    font-family: 'Lato';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url(${latoRegExt}) format('woff2');
    unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Lato';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url(${latoReg}) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
  /* latin-ext */
  @font-face {
    font-family: 'Lato';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: url(${latoBoldExt}) format('woff2');
    unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF;
  }
  /* latin */
  @font-face {
    font-family: 'Lato';
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: url(${latoBold}) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
  }
`;

const TOKENS_WITHOUT_VALIDATION = [
  '3ab5b9e1-1896-48d8-a2a4-c3921bc024d8',
  '59bc7a32-5dcb-4ad7-8b32-ed37a794ffa1',
];

function getValue(key: string, sp: URLSearchParams): string | undefined {
  return sp.has(key) ? (sp.get(key) ?? undefined) : undefined;
}

function getSelectedMode(sp: URLSearchParams): AppMode {
  const value = getValue('mode', sp);
  if (value === 'measurement') {
    return AppMode.Measurement;
  } else if (value === 'sizechart') {
    return AppMode.Sizechart;
  }
  return AppMode.Recommendation;
}

function App(): JSX.Element {
  const { setSideBarOpen } = useViewStore((store) => store);
  const { setEmail, setSubject, setPreviousPurchaseID, setScanID } =
    useInputStore((store) => store);
  const { applyParams } = useSessionStore((store) => store);
  const { setDisableCaptureValidation } = useScannerStore((store) => store);
  const sp = new URLSearchParams(globalThis.location.search);

  useEffect(() => {
    const token = getValue('token', sp);
    applyParams({
      token,
      mode: getSelectedMode(sp),
      context: getValue('context', sp),
      productID: getValue('product_id', sp),
      brandName: getValue('product_brand_name', sp),
      genderAge: getValue('product_gender_age', sp),
      productName: getValue('product_name', sp),
      callbackUrl: getValue('callback_url', sp),
      loadFromQr: getValue('load_from_qr', sp)?.toLocaleLowerCase() === 'true',
      session: getValue('session', sp),
      sizeLocale: getValue('size_locale', sp),
      storeFinderUrl: getValue('store_finder_url', sp),
      deviceId: getValue('device_id', sp),
      widgetComponentType: getValue('component_type', sp),
      paperType: getValue('paper_type', sp),
      skipOnboarding:
        getValue('skip_onboarding', sp)?.toLocaleLowerCase() === 'true',
    });
    const pp = getValue('previous_purchase_id', sp);
    if (pp) {
      setPreviousPurchaseID(pp);
    }
    const sID = getValue('scan_id', sp);
    if (sID) {
      setScanID(sID);
    }
    const e = getValue('subject', sp) || getValue('email', sp);
    if (e) {
      setEmail(e); // TODO: We should probably try to separate email and subject.
      setSubject(e);
    }
    if (token) {
      setDisableCaptureValidation(TOKENS_WITHOUT_VALIDATION.includes(token));
    }
  }, []);

  // Checks the width of the sidebar to see if its visable or not.
  useEffect(() => {
    let closed = true;
    const root = document.querySelectorAll('html')[0];
    const observer = new ResizeObserver(() => {
      if (root.offsetWidth !== 0 && closed) {
        closed = false;
        setSideBarOpen(true);
        usageTracker.track(UsageTrackerEvent.OpenSidebar, {
          experience: getSelectedMode(sp),
          widget_component_type: getValue('component_type', sp),
          set_paper_type: getValue('paper_type', sp),
        });
      } else if (root.offsetWidth === 0 && !closed) {
        closed = true;
        setSideBarOpen(false);
        usageTracker.track(UsageTrackerEvent.CloseSidebar);
      }
    });
    observer.observe(root);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <ApiProvider>
      <ConfigurationProvider>
        <Navigation />
      </ConfigurationProvider>
    </ApiProvider>
  );
}

export default App;
